<template>
  <div class="rawang-dashboard">
    <div class="container">
      <div class="header">
        <h1>Selgate Rawang Specialist Hospital</h1>
        <p>Live overview of revenue, patients, admissions and bed occupancy</p>
      </div>

      <div class="filter-description">
        <p>
          <i class="fa-solid fa-circle-info"></i>
          Dashboard for Selgate Rawang Specialist Hospital. Data is based on the selected calendar date range; changing the dates will auto-refresh the dashboard.
          <span v-if="lastUpdated" class="last-updated">Last updated: {{ lastUpdated }}</span>
        </p>
      </div>

      <div class="filters">
        <div class="filter-group">
          <div class="date-range-inputs">
            <label class="date-field">
              <span>From</span>
              <input
                type="date"
                v-model="startDateInput"
              >
            </label>
            <label class="date-field">
              <span>To</span>
              <input
                type="date"
                v-model="endDateInput"
              >
            </label>
          </div>
          <button class="export-btn refresh-btn" @click="fetchDashboard" :disabled="isLoading">
            <i class="fa-solid fa-rotate-right" :class="{ spinning: isLoading }"></i>
            Refresh
          </button>
        </div>
        <div class="auto-loading-bar" v-if="isLoading">
          <div class="auto-loading-inner"></div>
        </div>
      </div>

      <!-- Sections with h2 + Finance-style summary cards -->
      <section v-for="section in topSections" :key="section.title" class="kpi-section">
        <h2 class="section-title">{{ section.title }}</h2>
        <div class="summary-cards">
          <div
            v-for="metric in section.metrics"
            :key="metric.label"
            class="card"
          >
            <div class="card-icon">
              <i class="fa-solid" :class="metric.icon || 'fa-chart-line'"></i>
            </div>
            <div class="card-content">
              <div class="card-header">
                <h3>{{ metric.label }}</h3>
                <div v-if="metric.tooltip" class="info-tooltip">
                  <i class="fa-solid fa-circle-info"></i>
                  <div class="tooltip-content">
                    {{ metric.tooltip }}
                    <div v-if="metric.formula" class="tooltip-formula">{{ metric.formula }}</div>
                  </div>
                </div>
              </div>
              <p class="amount">
                <CurrencyDisplay v-if="metric.rawValue != null" :value="metric.rawValue" size="large" />
                <span v-else>{{ metric.value || '—' }}</span>
              </p>
            </div>
          </div>
        </div>
      </section>

      <div v-if="error" class="error-banner">
      <i class="fa-solid fa-triangle-exclamation"></i>
      <div>
        <p>{{ error }}</p>
        <p class="error-sub">Please try again or contact IT if this keeps happening.</p>
      </div>
      </div>

      <div v-if="isLoading && !widgets.length" class="loading-state">
      <div class="spinner"></div>
      <p>Loading Rawang dashboard data...</p>
      </div>

      <div v-else class="dashboard-grid">
      <!-- Row 1 -->
      <div class="card">
        <div class="card-header">
          <h2>{{ paymentModeWidget ? paymentModeWidget.header : 'Daily Collection By Payment Mode' }}</h2>
        </div>
        <div class="card-body">
          <div v-if="paymentModeWidget" class="chart-shell">
            <Bar
              :data="paymentModeChartData"
              :options="horizontalBarOptions"
            />
          </div>
          <p v-else class="empty-text">No payment mode data available.</p>
        </div>
      </div>

      <div class="card counts-card">
        <div class="card-header">
          <h2>{{ ipSummaryWidget ? ipSummaryWidget.header : 'IP Summary Details' }}</h2>
        </div>
        <div class="card-body ip-summary">
          <div
            v-for="item in ipSummaryWidget?.payloads || []"
            :key="item[ipSummaryWidget.entityKey]"
            class="ip-pill"
          >
            <span class="pill-label">{{ item[ipSummaryWidget.entityKey] }}</span>
            <span class="pill-value">{{ item.count_data }}</span>
          </div>
          <p v-if="!ipSummaryWidget" class="empty-text">No IP summary data.</p>
        </div>
      </div>

      <div class="card">
        <div class="card-header">
          <h2>{{ payerRevenueWidget ? payerRevenueWidget.header : 'Daily Payer Wise Revenue' }}</h2>
        </div>
        <div class="card-body">
          <div v-if="payerRevenueWidget" class="chart-shell">
            <Bar
              :data="payerRevenueChartData"
              :options="horizontalBarOptions"
            />
          </div>
          <p v-else class="empty-text">No payer revenue data.</p>
        </div>
      </div>

      <div class="card">
        <div class="card-header">
          <h2>{{ visitRevenueWidget ? visitRevenueWidget.header : 'Daily Revenue By Visit Type' }}</h2>
        </div>
        <div class="card-body">
          <div v-if="visitRevenueWidget" class="chart-shell">
            <Bar
              :data="visitRevenueChartData"
              :options="visitTypeBarOptions"
            />
          </div>
          <p v-else class="empty-text">No visit type revenue data.</p>
        </div>
      </div>

      <!-- Row 2 -->
      <div class="card">
        <div class="card-header">
          <h2>{{ billingStatusWidget ? billingStatusWidget.header : 'Billing Status By Bill Count' }}</h2>
          <p class="header-helper">OP = Outpatient, IP = Inpatient</p>
        </div>
        <div class="card-body table-wrapper">
          <table v-if="billingStatusWidget">
            <thead>
              <tr>
                <th>Status</th>
                <th>Total</th>
                <th>OP</th>
                <th>IP</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in billingStatusWidget.payloads" :key="row.Status">
                <td>{{ row.Status }}</td>
                <td>{{ toInt(row.TOTAL) }}</td>
                <td>{{ toInt(row.OP) }}</td>
                <td>{{ toInt(row.IP) }}</td>
              </tr>
            </tbody>
          </table>
          <p v-else class="empty-text">No billing status data.</p>
        </div>
      </div>

      <div class="card">
        <div class="card-header">
          <h2>Collection Amount by Payer Type</h2>
        </div>
        <div class="card-body">
          <div v-if="payerCollectionWidget" class="chart-shell">
            <Bar
              :data="payerCollectionChartData"
              :options="verticalBarOptions"
            />
          </div>
          <p v-else class="empty-text">No payer collection data.</p>
        </div>
      </div>

      <!-- Row 3 -->
      <div class="card">
        <div class="card-header">
          <h2>{{ admissionWardWidget ? admissionWardWidget.header : 'Admission By Ward' }}</h2>
        </div>
        <div class="card-body">
          <div v-if="admissionWardWidget" class="chart-shell">
            <Pie
              :data="admissionWardChartData"
              :options="pieOptions"
            />
          </div>
          <p v-else class="empty-text">No ward admission data.</p>
        </div>
      </div>

      <div class="card">
        <div class="card-header">
          <h2>{{ bedOccupancyWidget ? bedOccupancyWidget.header : 'Bed Occupancy' }}</h2>
        </div>
        <div class="card-body">
          <div v-if="bedOccupancyWidget" class="progress-list">
            <div
              v-for="item in bedOccupancyDisplayPayloads"
              :key="item[bedOccupancyWidget.entityKey]"
              class="progress-item"
            >
              <div class="label-row">
                <span class="label">{{ formatBedOccupancyLabel(item[bedOccupancyWidget.entityKey]) }}</span>
                <span class="value">{{ toInt(item.capped_count_data) }}</span>
              </div>
              <div class="bar-track">
                <div
                  class="bar-fill"
                  :style="{ width: getPercent(item.capped_count_data, bedOccupancyMax) + '%' }"
                ></div>
              </div>
            </div>
          </div>
          <p v-else class="empty-text">No bed occupancy data.</p>
        </div>
      </div>

      <div class="op-visits-row">
        <div class="card">
          <div class="card-header">
            <h2>{{ opSpecialtyWidget ? opSpecialtyWidget.header : 'OP Visits By Specialty' }}</h2>
          </div>
          <div class="card-body">
            <div v-if="opSpecialtyWidget" class="op-visits-list">
              <div
                v-for="item in opSpecialtyWidget.payloads"
                :key="item[opSpecialtyWidget.entityKey]"
                class="pill-row"
              >
                <span class="pill-label">{{ item[opSpecialtyWidget.entityKey] }}</span>
                <span class="pill-value">{{ toInt(item.count_data) }}</span>
              </div>
            </div>
            <p v-else class="empty-text">No specialty data.</p>
          </div>
        </div>

        <div class="card">
          <div class="card-header">
            <h2>{{ opDoctorWidget ? opDoctorWidget.header : 'OP Visits By Doctor' }}</h2>
          </div>
          <div class="card-body">
            <div v-if="opDoctorWidget" class="op-visits-list">
              <div
                v-for="item in opDoctorWidget.payloads"
                :key="item[opDoctorWidget.entityKey]"
                class="pill-row"
              >
                <span class="pill-label">{{ item[opDoctorWidget.entityKey] }}</span>
                <span class="pill-value">{{ toInt(item.count_data) }}</span>
              </div>
            </div>
            <p v-else class="empty-text">No doctor data.</p>
          </div>
        </div>
      </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, onUnmounted, ref, watch } from 'vue'
import axios from 'axios'
import { Bar, Pie } from 'vue-chartjs'
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Tooltip,
  Legend
} from 'chart.js'

ChartJS.register(CategoryScale, LinearScale, BarElement, ArcElement, Tooltip, Legend)

const widgets = ref([])
const isLoading = ref(false)
const error = ref(null)
const lastUpdated = ref(null)
const autoRefreshTimeout = ref(null)

const startDate = ref(getMonthStart())
const endDate = ref(getMonthEnd())

const topKpi = ref({
  revenue: 0,
  operating_expenses: 0,
  ebitda: 0,
  ebitda_margin: 0,
  opd_per_adm: 0,
  new_opv_ratio: 0
})

const hisDerivedRevenue = computed(() => {
  const payerW = widgets.value.find(x => x.header === 'Daily Payer Wise Revenue')
  if (payerW?.payloads?.length) {
    return payerW.payloads.reduce((sum, p) => sum + (Number(p.count_data) || 0), 0)
  }
  const visitW = widgets.value.find(x => x.header === 'Daily Revenue By Visit Type')
  if (visitW?.payloads?.length) {
    return visitW.payloads.reduce((sum, p) => sum + (Number(p.count_data) || 0), 0)
  }
  return null
})

const displayKpi = computed(() => {
  const rev = hisDerivedRevenue.value ?? topKpi.value.revenue
  return {
    ...topKpi.value,
    revenue: rev
  }
})

const sshrTargets = ref({ target: null, budget: null })
const ytdWidgets = ref([])

const hisDerivedOpdAdm = computed(() => {
  const opW = widgets.value.find(x => x.header === 'OP Visits By Specialty')
  const ipW = widgets.value.find(x => x.header === 'IP Summary Details')
  if (!opW?.payloads?.length || !ipW?.payloads?.length) return null
  const opVisits = opW.payloads.reduce((sum, p) => sum + (Number(p.count_data) || 0), 0)
  const admissionRow = ipW.payloads.find(p => {
    const key = ipW.entityKey
    const val = p[key] || ''
    return String(val).toLowerCase().includes('admission')
  })
  const admissions = admissionRow ? Number(admissionRow.count_data) || 0 : 0
  if (admissions <= 0) return null
  return opVisits / admissions
})

const displayOpdAdm = computed(() => hisDerivedOpdAdm.value ?? topKpi.value.opd_per_adm ?? 0)

const hisDerivedNewOpvRatio = computed(() => {
  const payload = ytdWidgets.value
  if (!payload?.length) return null
  const opW = payload.find(x => x.header === 'OP Visits By Specialty')
  if (!opW?.payloads?.length) return null
  const totalOpv = opW.payloads.reduce((sum, p) => sum + (Number(p.count_data) || 0), 0)
  if (totalOpv <= 0) return null
  const newW = payload.find(w => {
    const h = (w.header || '').toLowerCase()
    return h.includes('new') || h.includes('first') || h.includes('return') || h.includes('visit type')
  })
  if (newW?.payloads?.length && newW.entityKey) {
    const newOpv = newW.payloads
      .filter(p => {
        const val = String(p[newW.entityKey] || '').toLowerCase()
        return val.includes('new') || val.includes('first')
      })
      .reduce((sum, p) => sum + (Number(p.count_data) || 0), 0)
    if (newOpv >= 0) return (newOpv / totalOpv) * 100
  }
  return null
})

const displayNewOpvRatio = computed(() => hisDerivedNewOpvRatio.value ?? topKpi.value.new_opv_ratio ?? 0)

const topSections = computed(() => [
  {
    title: 'Financial',
    metrics: [
      { label: 'Revenue', rawValue: displayKpi.value.revenue, icon: 'fa-chart-line' },
      { label: 'Operating Expenses', rawValue: displayKpi.value.operating_expenses, icon: 'fa-receipt' },
      { label: 'EBITDA', rawValue: displayKpi.value.ebitda, icon: 'fa-chart-simple' },
      { label: 'EBITDA Margin', value: `${Number(displayKpi.value.ebitda_margin || 0).toFixed(1)}%`, icon: 'fa-percent' },
      {
        label: 'OPD / ADM',
        value: Number(displayOpdAdm.value).toFixed(1),
        icon: 'fa-divide',
        tooltip: 'Outpatient visits per admission',
        formula: 'OPD/ADM = Outpatient Visits ÷ Admissions (from HIS)'
      },
      {
        label: 'New OPV / Total OPV',
        value: `${Number(displayNewOpvRatio.value).toFixed(1)}%`,
        icon: 'fa-chart-pie',
        tooltip: 'New outpatient visits as % of total OP visits (YTD)',
        formula: 'New OPV ÷ Total OPV × 100% (HIS YTD). Fallback: KPI snapshot'
      },
      { label: 'Monthly Target', rawValue: sshrTargets.value.target, icon: 'fa-bullseye' },
      { label: 'Annual Budget', rawValue: sshrTargets.value.budget, icon: 'fa-coins' }
    ]
  }
])

function getMonthStart () {
  const d = new Date()
  return new Date(d.getFullYear(), d.getMonth(), 1)
}

function getMonthEnd () {
  const d = new Date()
  return new Date(d.getFullYear(), d.getMonth() + 1, 0)
}

function formatDateLocal (d) {
  const year = d.getFullYear()
  const month = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

const formattedRange = computed(() => {
  return `${formatDateLocal(startDate.value)} → ${formatDateLocal(endDate.value)}`
})

const startDateInput = computed({
  get () {
    return formatDateLocal(startDate.value)
  },
  set (val) {
    if (!val) return
    const newStart = new Date(val)
    if (isNaN(newStart.getTime())) return
    // Ensure start is not after end
    if (newStart > endDate.value) {
      endDate.value = new Date(newStart)
    }
    startDate.value = newStart
  }
})

const endDateInput = computed({
  get () {
    return formatDateLocal(endDate.value)
  },
  set (val) {
    if (!val) return
    const newEnd = new Date(val)
    if (isNaN(newEnd.getTime())) return
    // Ensure end is not before start
    if (newEnd < startDate.value) {
      startDate.value = new Date(newEnd)
    }
    endDate.value = newEnd
  }
})

const hisApi = axios.create({
  baseURL: 'https://api-svsb.selangkah.my/HIS',
  timeout: 15000,
  headers: {
    accept: '*/*'
  }
})

const internalApi = axios.create({
  baseURL: import.meta.env.VITE_API_URL,
  timeout: import.meta.env.VITE_API_TIMEOUT,
  headers: {
    'Content-Type': 'application/json'
  }
})

internalApi.interceptors.request.use(config => {
  const token = sessionStorage.getItem('token')
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }
  return config
})

const getWidgetByHeader = (header) =>
  computed(() => widgets.value.find(w => w.header === header))

const paymentModeWidget = getWidgetByHeader('Daily Collection By Payment Mode')
const ipSummaryWidget = getWidgetByHeader('IP Summary Details')
const payerRevenueWidget = getWidgetByHeader('Daily Payer Wise Revenue')
const visitRevenueWidget = getWidgetByHeader('Daily Revenue By Visit Type')
const billingStatusWidget = getWidgetByHeader('Billing Status By Bill Count')
const payerCollectionWidget = getWidgetByHeader('Daily Collection By Payer')
const opSpecialtyWidget = getWidgetByHeader('OP Visits By Specialty')
const opDoctorWidget = getWidgetByHeader('OP Visits By Doctor')
const admissionWardWidget = getWidgetByHeader('Admission By Ward')
const bedOccupancyWidget = getWidgetByHeader('Bed Occupancy')
const BED_OCCUPANCY_CAPACITY = 38

const paymentModeMax = computed(
  () => getMax(paymentModeWidget.value?.payloads, 'count_data')
)
const payerRevenueMax = computed(
  () => getMax(payerRevenueWidget.value?.payloads, 'count_data')
)
const visitRevenueMax = computed(
  () => getMax(visitRevenueWidget.value?.payloads, 'count_data')
)
const payerCollectionMax = computed(
  () => getMax(payerCollectionWidget.value?.payloads, 'count_data')
)
const bedOccupancyDisplayPayloads = computed(() => {
  const widget = bedOccupancyWidget.value
  const payloads = widget?.payloads || []
  const entityKey = widget?.entityKey

  const occupiedRaw = payloads
    .filter((item) => /occupied/i.test(String(item?.[entityKey] || '')))
    .reduce((sum, item) => sum + (Number(item?.count_data) || 0), 0)

  const occupiedCount = Math.min(Math.max(occupiedRaw, 0), BED_OCCUPANCY_CAPACITY)
  const vacantCount = Math.max(BED_OCCUPANCY_CAPACITY - occupiedCount, 0)

  return payloads.map((item) => {
    const label = String(item?.[entityKey] || '')
    if (/occupied/i.test(label)) {
      return { ...item, capped_count_data: occupiedCount }
    }
    if (/vacant/i.test(label)) {
      return { ...item, capped_count_data: vacantCount }
    }
    return {
      ...item,
      capped_count_data: Math.min(Math.max(Number(item.count_data) || 0, 0), BED_OCCUPANCY_CAPACITY)
    }
  })
})

const bedOccupancyMax = computed(() => BED_OCCUPANCY_CAPACITY)

const baseBarOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      display: false
    },
    tooltip: {
      callbacks: {
        label (ctx) {
          // Use the raw dataset value to avoid Chart.js parsing quirks
          const raw = Array.isArray(ctx.raw) ? ctx.raw[0] : ctx.raw
          const value = Number(raw) || 0
          return `RM ${toAmount(value)}`
        }
      }
    }
  },
  scales: {
    x: {
      ticks: { color: '#475569', font: { size: 10 } },
      grid: { display: false }
    },
    y: {
      ticks: { color: '#64748b', font: { size: 10 } },
      grid: { color: '#e2e8f0' }
    }
  }
}

const horizontalBarOptions = computed(() => ({
  ...baseBarOptions,
  indexAxis: 'y'
}))

const verticalBarOptions = computed(() => ({
  ...baseBarOptions,
  indexAxis: 'x'
}))

const VISIT_TYPE_AXIS_LABELS = {
  IP: 'Inpatient',
  OP: 'Outpatient',
  ER: 'Emergency Department'
}

const VISIT_TYPE_BAR_COLORS = {
  IP: '#6366f1',
  OP: '#22c55e',
  ER: '#f97316'
}

function formatVisitTypeAxisLabel (code) {
  if (code == null || code === '') return code
  const key = String(code).trim().toUpperCase()
  return VISIT_TYPE_AXIS_LABELS[key] || code
}

function visitTypeBarColor (code) {
  if (code == null || code === '') return '#64748b'
  const key = String(code).trim().toUpperCase()
  return VISIT_TYPE_BAR_COLORS[key] || '#0ea5e9'
}

function formatBedOccupancyLabel (label) {
  return String(label || '')
    .replace(/\(\s*\d+\s*\)/g, '')
    .trim()
}

const visitTypeBarOptions = computed(() => ({
  ...baseBarOptions,
  indexAxis: 'x',
  plugins: {
    ...baseBarOptions.plugins,
    legend: {
      display: false
    }
  }
}))

const paymentModeChartData = computed(() => {
  const w = paymentModeWidget.value
  if (!w) return { labels: [], datasets: [] }
  return {
    labels: w.payloads.map(p => p[w.entityKey]),
    datasets: [
      {
        label: 'Amount (RM)',
        data: w.payloads.map(p => p.count_data),
        backgroundColor: '#4f46e5'
      }
    ]
  }
})

const payerRevenueChartData = computed(() => {
  const w = payerRevenueWidget.value
  if (!w) return { labels: [], datasets: [] }
  return {
    labels: w.payloads.map(p => p[w.entityKey]),
    datasets: [
      {
        label: 'Revenue (RM)',
        data: w.payloads.map(p => p.count_data),
        backgroundColor: '#0ea5e9'
      }
    ]
  }
})

const visitRevenueChartData = computed(() => {
  const w = visitRevenueWidget.value
  if (!w) return { labels: [], datasets: [] }
  const key = w.entityKey
  return {
    labels: w.payloads.map(p => formatVisitTypeAxisLabel(p[key])),
    datasets: [
      {
        label: '',
        data: w.payloads.map(p => p.count_data),
        backgroundColor: w.payloads.map(p => visitTypeBarColor(p[key]))
      }
    ]
  }
})

const payerCollectionChartData = computed(() => {
  const w = payerCollectionWidget.value
  if (!w) return { labels: [], datasets: [] }
  return {
    labels: w.payloads.map(p => p[w.entityKey]),
    datasets: [
      {
        label: 'Collection (RM)',
        data: w.payloads.map(p => p.count_data),
        backgroundColor: ['#22c55e', '#0ea5e9']
      }
    ]
  }
})

const admissionWardChartData = computed(() => {
  const w = admissionWardWidget.value
  if (!w) return { labels: [], datasets: [] }
  return {
    labels: w.payloads.map(p => p[w.entityKey]),
    datasets: [
      {
        data: w.payloads.map(p => p.count_data),
        backgroundColor: [
          '#6366f1',
          '#22c55e',
          '#f97316',
          '#a855f7',
          '#0ea5e9',
          '#eab308',
          '#64748b',
          '#ec4899',
          '#14b8a6'
        ]
      }
    ]
  }
})

const pieOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      position: 'right',
      labels: {
        usePointStyle: true,
        boxWidth: 8,
        font: { size: 10 }
      }
    }
  }
}

function getMax (items, key) {
  if (!items || !items.length) return 0
  return items.reduce((max, item) => {
    const v = Number(item[key]) || 0
    return v > max ? v : max
  }, 0)
}

function getPercent (value, max) {
  const v = Number(value) || 0
  if (!max || max <= 0) return 0
  return Math.round((v / max) * 100)
}

function toAmount (value) {
  const v = Number(value) || 0
  return v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

function toInt (value) {
  const v = Number(value) || 0
  return Math.round(v)
}

async function fetchDashboard () {
  try {
    isLoading.value = true
    error.value = null

    const from = formatDateLocal(startDate.value)
    const to = formatDateLocal(endDate.value)

    const { data } = await hisApi.get('/sshDashboard', {
      params: {
        frDate: from,
        toDate: to,
        unitId: 0,
        userId: 0
      }
    })

    if (data?.Data?.payload && Array.isArray(data.Data.payload)) {
      widgets.value = data.Data.payload
      lastUpdated.value = data.Data.timestamp || null
    } else {
      widgets.value = []
      error.value = 'Live Rawang HIS data is temporarily unavailable.'
    }
  } catch (e) {
    widgets.value = []
    error.value = 'Failed to load live Rawang HIS dashboard data.'
  } finally {
    isLoading.value = false
  }
}

async function fetchTopKpi () {
  try {
    const targetDate = formatDateLocal(endDate.value)
    const { data } = await internalApi.get('/api/finance/rawang-kpi', {
      params: { date: targetDate }
    })
    topKpi.value = {
      revenue: data.revenue ?? 0,
      operating_expenses: data.operating_expenses ?? 0,
      ebitda: data.ebitda ?? 0,
      ebitda_margin: data.ebitda_margin ?? 0,
      opd_per_adm: data.opd_per_adm ?? 0,
      new_opv_ratio: data.new_opv_ratio ?? 0
    }
  } catch (e) {
  }
}

async function fetchSshrTargets () {
  try {
    const year = endDate.value.getFullYear()
    const { data } = await internalApi.get('/api/finance/rawang-sshr-targets', {
      params: { year }
    })
    sshrTargets.value = {
      target: data.target ?? null,
      budget: data.budget ?? null
    }
  } catch (e) {
  }
}

async function fetchYtdForNewOpv () {
  try {
    const year = endDate.value.getFullYear()
    const ytdFrom = `${year}-01-01`
    const ytdTo = formatDateLocal(endDate.value)
    const { data } = await hisApi.get('/sshDashboard', {
      params: { frDate: ytdFrom, toDate: ytdTo, unitId: 0, userId: 0 }
    })
    if (data?.Data?.payload && Array.isArray(data.Data.payload)) {
      ytdWidgets.value = data.Data.payload
    } else {
      ytdWidgets.value = []
    }
  } catch (e) {
    ytdWidgets.value = []
  }
}

onMounted(() => {
  fetchDashboard()
  fetchTopKpi()
  fetchSshrTargets()
  fetchYtdForNewOpv()
})

// Auto-refresh when date range changes (with small debounce)
watch([startDate, endDate], () => {
  if (autoRefreshTimeout.value) {
    clearTimeout(autoRefreshTimeout.value)
  }
  autoRefreshTimeout.value = setTimeout(() => {
    fetchDashboard()
    fetchTopKpi()
    fetchSshrTargets()
    fetchYtdForNewOpv()
  }, 500)
})

onUnmounted(() => {
  if (autoRefreshTimeout.value) {
    clearTimeout(autoRefreshTimeout.value)
  }
})
</script>

<style scoped>
.rawang-dashboard {
  padding: 24px;
  background: #f8fafc;
  min-height: 100vh;
  width: 100%;
}

.container {
  width: 100%;
  margin: 0 auto;
}

.header {
  margin-bottom: 32px;
}

.header h1 {
  font-size: 1.875rem;
  font-weight: 600;
  color: #1e293b;
  margin: 0 0 8px 0;
}

.header p {
  font-size: 0.875rem;
  color: #64748b;
  margin: 0;
}

.filter-description {
  margin-bottom: 16px;
  padding: 8px 12px;
  background: #f1f5f9;
  border-radius: 6px;
  border: 1px solid #e2e8f0;
}

.filter-description p {
  font-size: 0.813rem;
  color: #475569;
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0;
}

.filter-description i {
  color: #0ea5e9;
  font-size: 0.875rem;
}

.filters {
  margin-bottom: 24px;
}

.filter-group {
  display: flex;
  align-items: flex-end;
  gap: 12px;
  flex-wrap: wrap;
}

.auto-loading-bar {
  width: 100%;
  height: 3px;
  margin-top: 6px;
  border-radius: 999px;
  background: #e2e8f0;
  overflow: hidden;
}

.auto-loading-inner {
  width: 40%;
  height: 100%;
  background: linear-gradient(90deg, #0ea5e9, #22c55e);
  border-radius: 999px;
  animation: autoLoading 0.8s ease-in-out infinite alternate;
}

.date-range-inputs {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.date-field {
  display: flex;
  flex-direction: column;
  gap: 4px;
  font-size: 0.75rem;
  color: #64748b;
}

.date-field input[type="date"] {
  padding: 6px 10px;
  border-radius: 6px;
  border: 1px solid rgba(226, 232, 240, 0.9);
  background: #ffffff;
  font-size: 0.8rem;
  color: #0f172a;
}

.date-field input[type="date"]:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.1);
}

.refresh-btn {
  align-self: flex-end;
  margin-bottom: 2px;
}

.date-range-display {
  padding: 8px 16px;
  background: white;
  border-radius: 8px;
  border: 1px solid rgba(226, 232, 240, 0.8);
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.current-period {
  font-size: 1rem;
  font-weight: 500;
  color: #1e293b;
}

.export-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 6px 12px;
  background: #0ea5e9;
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 0.78rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.export-btn:hover:not(:disabled) {
  background: #0284c7;
  transform: translateY(-1px);
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
}

.export-btn:disabled {
  opacity: 0.7;
  cursor: default;
}

.export-btn i.spinning {
  animation: spin 1s linear infinite;
}

.kpi-section {
  margin-bottom: 32px;
}

.section-title {
  font-size: 1.125rem;
  font-weight: 600;
  color: #1e293b;
  margin: 0 0 12px 0;
}

.summary-cards {
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  gap: 12px;
}

.kpi-section .card {
  background: white;
  border-radius: 6px;
  padding: 16px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1), 0 1px 2px -1px rgba(0, 0, 0, 0.1);
  border: 1px solid rgba(226, 232, 240, 0.8);
  display: flex;
  align-items: flex-start;
  gap: 12px;
  height: 100%;
}

.kpi-section .card-icon {
  width: 40px;
  height: 40px;
  border-radius: 8px;
  background: #e0f2fe;
  color: #0ea5e9;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.25rem;
  flex-shrink: 0;
}

.kpi-section .card-content {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.kpi-section .card-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 4px;
}

.kpi-section .info-tooltip {
  position: relative;
  display: inline-flex;
}

.kpi-section .info-tooltip i {
  font-size: 0.75rem;
  color: #94a3b8;
  cursor: help;
}

.kpi-section .info-tooltip:hover i {
  color: #0ea5e9;
}

.kpi-section .tooltip-content {
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  bottom: 100%;
  margin-bottom: 6px;
  padding: 8px 10px;
  background: #1e293b;
  color: #f8fafc;
  font-size: 0.75rem;
  border-radius: 6px;
  max-width: 220px;
  white-space: normal;
  z-index: 10;
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.2s;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.2);
}

.kpi-section .tooltip-formula {
  margin-top: 4px;
  font-size: 0.7rem;
  color: #cbd5e1;
}

.kpi-section .info-tooltip:hover .tooltip-content {
  opacity: 1;
}

.kpi-section .card-content h3 {
  font-size: 0.75rem;
  font-weight: 500;
  color: #64748b;
  margin: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.kpi-section .amount {
  font-size: 1.125rem;
  font-weight: 600;
  color: #1e293b;
  margin: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

@media (max-width: 1536px) {
  .summary-cards {
    grid-template-columns: repeat(4, 1fr);
  }
}

@media (max-width: 1024px) {
  .summary-cards {
    grid-template-columns: repeat(3, 1fr);
  }
}

@media (max-width: 768px) {
  .summary-cards {
    grid-template-columns: repeat(2, 1fr);
  }
}

.error-banner {
  display: flex;
  gap: 12px;
  padding: 10px 14px;
  border-radius: 10px;
  border: 1px solid #fecaca;
  background: #fef2f2;
  color: #b91c1c;
  margin-bottom: 16px;
}

.error-banner i {
  margin-top: 3px;
}

.error-sub {
  font-size: 0.8rem;
  color: #991b1b;
}

.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 48px 0;
  gap: 12px;
  color: #64748b;
}

.spinner {
  width: 32px;
  height: 32px;
  border-radius: 999px;
  border: 3px solid #e2e8f0;
  border-top-color: #2563eb;
  animation: spin 0.9s linear infinite;
}

.dashboard-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: 20px;
}

.card {
  background: white;
  border-radius: 16px;
  padding: 16px 16px 18px;
  box-shadow: 0 10px 25px rgba(15, 23, 42, 0.06);
  border: 1px solid rgba(226, 232, 240, 0.9);
}

.card-header {
  margin-bottom: 12px;
  display: flex;
  align-items: center;
  gap: 8px;
}

.card-header h2 {
  font-size: 0.98rem;
  font-weight: 600;
  color: #0f172a;
}

.header-helper {
  margin-top: 2px;
  font-size: 0.72rem;
  color: #94a3b8;
}

.card-body {
  font-size: 0.85rem;
}

.chart-shell {
  position: relative;
  width: 100%;
  min-height: 220px;
}

.counts-card .card-body {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(110px, 1fr));
  gap: 10px;
}

.ip-summary {
  min-height: 120px;
}

.ip-pill {
  background: #eff6ff;
  border-radius: 12px;
  padding: 10px 12px;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.ip-pill:nth-child(2n) {
  background: #ecfeff;
}

.pill-label {
  font-size: 0.8rem;
  color: #475569;
}

.pill-value {
  font-size: 1.1rem;
  font-weight: 600;
  color: #1d4ed8;
}

.progress-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.progress-item {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.label-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
}

.label {
  font-size: 0.8rem;
  color: #475569;
}

.value {
  font-size: 0.8rem;
  font-weight: 500;
  color: #0f172a;
}

.bar-track {
  width: 100%;
  height: 8px;
  border-radius: 999px;
  background: #e5e7eb;
  overflow: hidden;
}

.bar-fill {
  height: 100%;
  border-radius: 999px;
  background: linear-gradient(90deg, #22c55e, #16a34a);
}

.bar-fill.alt {
  background: linear-gradient(90deg, #2563eb, #0ea5e9);
}

.bar-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.bar-item {
  display: grid;
  grid-template-columns: minmax(0, 1.5fr) minmax(0, 3fr) auto;
  align-items: center;
  gap: 8px;
}

.table-wrapper {
  overflow-x: auto;
}

table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.8rem;
}

th,
td {
  padding: 6px 8px;
  border-bottom: 1px solid #e5e7eb;
  text-align: left;
}

th {
  font-weight: 600;
  color: #64748b;
  background: #f8fafc;
}

.op-visits-row {
  grid-column: 1 / -1;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 20px;
  align-items: start;
}

.op-visits-row .card {
  min-width: 0;
}

.op-visits-list {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.pill-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
  padding: 6px 8px;
  border-radius: 10px;
  background: #f1f5f9;
}

.pie-legend {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.pie-row {
  display: flex;
  align-items: center;
  gap: 8px;
}

.color-dot {
  width: 10px;
  height: 10px;
  border-radius: 999px;
  background: linear-gradient(135deg, #6366f1, #22c55e);
}

.empty-text {
  font-size: 0.8rem;
  color: #94a3b8;
}

@media (max-width: 768px) {
  .dashboard {
    padding: 16px;
  }

  .dashboard-grid {
    gap: 12px;
  }

  .card {
    padding: 14px;
  }
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

@keyframes autoLoading {
  from {
    transform: translateX(-10%);
  }
  to {
    transform: translateX(110%);
  }
}
</style>

